import { toast } from 'react-toastify';

import {
  GET_AUDIT_RECORDS_REQUEST,
  GET_AUDIT_RECORDS_SUCCESS,
  GET_AUDIT_RECORDS_ERROR,
  GET_AUDIT_ITEMS_REQUEST,
  GET_AUDIT_ITEMS_SUCCESS,
  GET_AUDIT_ITEMS_ERROR,
  GET_ASSET_AUDITS_REQUEST,
  GET_ASSET_AUDITS_SUCCESS,
  GET_ASSET_AUDITS_ERROR,
  CREATE_AUDIT_REQUEST,
  CREATE_AUDIT_SUCCESS,
  CREATE_AUDIT_ERROR,
  DELETE_AUDIT_REQUEST,
  DELETE_AUDIT_SUCCESS,
  DELETE_AUDIT_ERROR,
  SCAN_AUDIT_ITEM_REQUEST,
  SCAN_AUDIT_ITEM_SUCCESS,
  SCAN_AUDIT_ITEM_ERROR,
  COMPLETE_AUDIT_REQUEST,
  COMPLETE_AUDIT_SUCCESS,
  COMPLETE_AUDIT_ERROR,
  RECONCILE_AUDIT_REQUEST,
  RECONCILE_AUDIT_SUCCESS,
  RECONCILE_AUDIT_ERROR,
} from './auditActionTypes';
import auditConstants from './auditConstants';
import call from '../../../core/services/http';

/**
 * Every thunk here used to carry its own forty-line copy of the same
 * try/catch: one branch for 400, one for 404 that dropped the message on the
 * floor, and one for everything else. `run` is that block, once.
 *
 * All of them resolve to `{ ok, data, message }` so a screen can react to a
 * save without watching a boolean in the store.
 */
const messageFrom = (error, fallback = 'Sorry, something went wrong') =>
  error?.response?.data?.message || fallback;

async function run({ dispatch, types: [REQUEST, SUCCESS, ERROR], request, onSuccess, notify = true }) {
  dispatch({ type: REQUEST, isLoading: true, isSuccess: false });
  try {
    const response = await call(...request);
    const body = response.data;
    if (body.status) {
      dispatch({ type: SUCCESS, isLoading: false, isSuccess: true, payload: body, message: body.message });
      if (notify && body.message) toast.success(body.message);
      if (onSuccess) onSuccess(body);
      return { ok: true, data: body.data, message: body.message, body };
    }
    dispatch({ type: ERROR, isLoading: false, isSuccess: false, message: body.message });
    toast.error(body.message);
    return { ok: false, message: body.message };
  } catch (error) {
    const message = messageFrom(error);
    dispatch({ type: ERROR, isLoading: false, isSuccess: false, message });
    toast.error(message);
    return { ok: false, message };
  }
}

export const getAuditRecords = (payload = {}) => async (dispatch) =>
  run({
    dispatch,
    types: [GET_AUDIT_RECORDS_REQUEST, GET_AUDIT_RECORDS_SUCCESS, GET_AUDIT_RECORDS_ERROR],
    request: ['get', auditConstants.GET_AUDIT_RECORDS(payload)],
    notify: false,
  });

export const getAuditItems = (payload) => async (dispatch) =>
  run({
    dispatch,
    types: [GET_AUDIT_ITEMS_REQUEST, GET_AUDIT_ITEMS_SUCCESS, GET_AUDIT_ITEMS_ERROR],
    request: ['get', auditConstants.GET_AUDIT_ITEMS(payload)],
    notify: false,
  });

export const getAssetAuditRecords = (payload) => async (dispatch) =>
  run({
    dispatch,
    types: [GET_ASSET_AUDITS_REQUEST, GET_ASSET_AUDITS_SUCCESS, GET_ASSET_AUDITS_ERROR],
    request: ['get', auditConstants.GET_ASSET_AUDIT_RECORDS(payload)],
    notify: false,
  });

export const createAuditRecord = (payload) => async (dispatch) =>
  run({
    dispatch,
    types: [CREATE_AUDIT_REQUEST, CREATE_AUDIT_SUCCESS, CREATE_AUDIT_ERROR],
    request: ['post', auditConstants.CREATE_AUDIT_RECORD, payload],
  });

export const deleteAuditRecord = (id) => async (dispatch) =>
  run({
    dispatch,
    types: [DELETE_AUDIT_REQUEST, DELETE_AUDIT_SUCCESS, DELETE_AUDIT_ERROR],
    request: ['delete', auditConstants.DELETE_AUDIT_RECORD(id)],
  });

/**
 * One scan. `payload` is `{ barcode_number }` — the server decides whether that
 * is a normal count, a misplacement, or an asset that is not on the checklist
 * at all, so the scanner does not need the branch loaded to know.
 *
 * Quiet by default: the caller reports the outcome itself, because a scanner
 * fires many times a second and a toast per scan is unreadable.
 */
export const scanAuditItem = (id, payload) => async (dispatch) =>
  run({
    dispatch,
    types: [SCAN_AUDIT_ITEM_REQUEST, SCAN_AUDIT_ITEM_SUCCESS, SCAN_AUDIT_ITEM_ERROR],
    request: ['post', auditConstants.SCAN_AUDIT_ITEM(id), payload],
    notify: false,
  });

export const completeAudit = (id) => async (dispatch) =>
  run({
    dispatch,
    types: [COMPLETE_AUDIT_REQUEST, COMPLETE_AUDIT_SUCCESS, COMPLETE_AUDIT_ERROR],
    request: ['post', auditConstants.COMPLETE_AUDIT(id), {}],
  });

export const reconcileAuditRecord = (id, payload) => async (dispatch) =>
  run({
    dispatch,
    types: [RECONCILE_AUDIT_REQUEST, RECONCILE_AUDIT_SUCCESS, RECONCILE_AUDIT_ERROR],
    request: ['post', auditConstants.RECONCILE_AUDIT_RECORD(id), payload],
  });
